import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { toast } from "sonner";
import { 
  Download, 
  ArrowLeft,
  Brain,
  Heart,
  Compass,
  BookOpen,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Sparkles,
  Target,
  Calendar,
  Loader2,
  ChevronRight
} from "lucide-react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_alma-metrics/artifacts/usza7456_Alquimia.png";

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

export default function ResultsPage() {
  const navigate = useNavigate();
  const [results, setResults] = useState(null);
  const [personalizedMessage, setPersonalizedMessage] = useState(null);
  const [loadingMessage, setLoadingMessage] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const reportRef = useRef(null);

  useEffect(() => {
    const storedResults = sessionStorage.getItem("evaluationResults");
    if (storedResults) {
      const parsed = JSON.parse(storedResults);
      setResults(parsed);
      fetchPersonalizedMessage(parsed);
    } else {
      navigate("/");
    }
  }, [navigate]);

  const fetchPersonalizedMessage = async (data) => {
    setLoadingMessage(true);
    try {
      const response = await axios.post(`${API}/report/personalize`, {
        score_global: data.score_global,
        disonancias: data.disonancias,
        valores_seleccionados: data.answers_summary?.valores_seleccionados || []
      });
      if (response.data.personalized) {
        setPersonalizedMessage(response.data.mensaje_personalizado);
      }
    } catch (error) {
      console.error("Error fetching personalized message:", error);
    } finally {
      setLoadingMessage(false);
    }
  };

  const generatePDF = async () => {
    setGeneratingPDF(true);
    toast.info("Generando tu reporte PDF...");
    
    try {
      const element = reportRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        backgroundColor: "#FFF9F5",
        logging: false,
        useCORS: true
      });
      
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4"
      });
      
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save("Reporte-Coherencia-Emocional-AlquimiaDelAlma.pdf");
      toast.success("¡Reporte descargado exitosamente!");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Error al generar el PDF");
    } finally {
      setGeneratingPDF(false);
    }
  };

  if (!results) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-cream to-cream-alt flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-coral animate-spin" />
      </div>
    );
  }

  const { 
    score_global, 
    componentes, 
    clasificacion, 
    clasificacion_mensaje, 
    disonancias, 
    roadmap, 
    reflexion_ontologica 
  } = results;

  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-500";
    if (score >= 60) return "text-amber-warm";
    if (score >= 40) return "text-orange-500";
    return "text-red-500";
  };

  const getSeverityBadge = (severidad) => {
    if (severidad === "alta") return "severity-alta";
    if (severidad === "moderada") return "severity-moderada";
    return "severity-baja";
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-cream to-cream-alt">
      {/* Header */}
      <header className="sticky top-0 z-50 glass-card border-b border-plum/5">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate("/")}
            className="text-plum-light hover:text-plum hover:bg-plum/5"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Inicio
          </Button>
          <Button
            onClick={generatePDF}
            disabled={generatingPDF}
            className="btn-coral"
            data-testid="download-pdf-btn"
          >
            {generatingPDF ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Descargar PDF
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main ref={reportRef} className="py-8 px-4 pdf-container">
        <div className="max-w-4xl mx-auto space-y-12">
          {/* Score Section */}
          <motion.section
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-center"
          >
            <motion.div variants={fadeInUp} className="flex justify-center mb-6">
              <img src={LOGO_URL} alt="Alquimia del Alma" className="h-16 w-16 rounded-full shadow-soft" />
            </motion.div>
            
            <motion.h1 
              variants={fadeInUp}
              className="font-heading text-2xl sm:text-3xl font-bold text-plum mb-2"
            >
              Tu Índice de Coherencia Emocional
            </motion.h1>
            
            <motion.p 
              variants={fadeInUp}
              className="text-plum-muted text-sm mb-8"
            >
              {new Date().toLocaleDateString("es-ES", { 
                year: "numeric", 
                month: "long", 
                day: "numeric" 
              })}
            </motion.p>

            {/* Circular Score Gauge */}
            <motion.div 
              variants={fadeInUp}
              className="relative w-64 h-64 mx-auto mb-8"
            >
              <svg className="w-full h-full transform -rotate-90" viewBox="0 0 160 160">
                {/* Background circle */}
                <circle
                  cx="80"
                  cy="80"
                  r="70"
                  fill="none"
                  stroke="#F0E6DE"
                  strokeWidth="12"
                />
                {/* Progress circle */}
                <circle
                  cx="80"
                  cy="80"
                  r="70"
                  fill="none"
                  stroke="url(#scoreGradientWarm)"
                  strokeWidth="12"
                  strokeLinecap="round"
                  strokeDasharray="440"
                  strokeDashoffset={440 - (440 * score_global) / 100}
                  className="score-gauge-animated"
                />
                <defs>
                  <linearGradient id="scoreGradientWarm" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#FF7F50" />
                    <stop offset="100%" stopColor="#9370DB" />
                  </linearGradient>
                </defs>
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className={`font-heading text-6xl font-bold ${getScoreColor(score_global)}`}>
                  {score_global}
                </span>
                <span className="text-plum-muted text-sm">de 100</span>
              </div>
            </motion.div>

            <motion.div variants={fadeInUp} className="glass-card-solid rounded-3xl p-6 max-w-md mx-auto">
              <div className={`inline-block px-4 py-1 rounded-full text-sm font-medium mb-3 ${
                clasificacion === "alta" ? "bg-green-100 text-green-600" :
                clasificacion === "moderada" ? "bg-amber-100 text-amber-600" :
                clasificacion === "baja" ? "bg-orange-100 text-orange-600" :
                "bg-red-100 text-red-600"
              }`}>
                Coherencia {clasificacion === "muy_baja" ? "Muy Baja" : clasificacion.charAt(0).toUpperCase() + clasificacion.slice(1)}
              </div>
              <h2 className="font-heading text-xl text-plum mb-2">{clasificacion_mensaje}</h2>
              
              {/* Personalized Message */}
              {loadingMessage ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 text-coral animate-spin" />
                </div>
              ) : personalizedMessage ? (
                <p className="text-plum-light text-sm leading-relaxed mt-4 italic">
                  "{personalizedMessage}"
                </p>
              ) : null}
            </motion.div>
          </motion.section>

          {/* Components Section */}
          <motion.section
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 
              variants={fadeInUp}
              className="font-heading text-2xl font-bold text-plum mb-6"
            >
              Tu Perfil de Coherencia
            </motion.h2>
            
            <motion.div 
              variants={fadeInUp}
              className="grid gap-4"
            >
              {[
                { key: "valores_acciones", label: "Valores-Acciones", icon: Brain, weight: "30%", color: "bg-coral" },
                { key: "coherencia_emocional", label: "Coherencia Emocional", icon: Heart, weight: "25%", color: "bg-lavender" },
                { key: "coherencia_narrativa", label: "Coherencia Narrativa", icon: BookOpen, weight: "25%", color: "bg-green-500" },
                { key: "congruencia_comunicacional", label: "Congruencia Comunicacional", icon: Compass, weight: "20%", color: "bg-amber-warm" }
              ].map((item, i) => {
                const score = componentes[item.key];
                return (
                  <div key={i} className="glass-card-solid rounded-2xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg ${item.color} flex items-center justify-center`}>
                          <item.icon className="w-4 h-4 text-white" />
                        </div>
                        <span className="text-plum font-medium">{item.label}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-plum-muted text-xs">{item.weight}</span>
                        <span className={`font-bold ${getScoreColor(score)}`}>{score}</span>
                      </div>
                    </div>
                    <div className="relative h-2 bg-plum/10 rounded-full overflow-hidden">
                      <motion.div 
                        className={`absolute inset-y-0 left-0 ${item.color} rounded-full`}
                        initial={{ width: 0 }}
                        animate={{ width: `${score}%` }}
                        transition={{ duration: 0.8, delay: i * 0.1 }}
                      />
                    </div>
                  </div>
                );
              })}
            </motion.div>
          </motion.section>

          {/* Dissonances Section */}
          {disonancias && disonancias.length > 0 && (
            <motion.section
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              <motion.h2 
                variants={fadeInUp}
                className="font-heading text-2xl font-bold text-plum mb-2"
              >
                Áreas de Disonancia Identificadas
              </motion.h2>
              <motion.p 
                variants={fadeInUp}
                className="text-plum-muted text-sm mb-6"
              >
                Estas son las principales áreas donde existe brecha entre tu interior y exterior
              </motion.p>

              <motion.div variants={fadeInUp}>
                <Accordion type="single" collapsible className="space-y-4">
                  {disonancias.map((dis, i) => (
                    <AccordionItem 
                      key={i} 
                      value={`item-${i}`}
                      className="glass-card-solid rounded-2xl border-0 overflow-hidden"
                    >
                      <AccordionTrigger 
                        className="px-6 py-4 hover:no-underline"
                        data-testid={`disonancia-trigger-${i}`}
                      >
                        <div className="flex items-start gap-4 text-left">
                          <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${
                            dis.severidad === "alta" ? "bg-red-100" : "bg-amber-100"
                          }`}>
                            <AlertTriangle className={`w-5 h-5 ${
                              dis.severidad === "alta" ? "text-red-500" : "text-amber-500"
                            }`} />
                          </div>
                          <div>
                            <h3 className="font-heading text-lg text-plum">{dis.nombre}</h3>
                            <span className={`inline-block text-xs px-2 py-0.5 rounded-full border mt-1 ${getSeverityBadge(dis.severidad)}`}>
                              Severidad {dis.severidad}
                            </span>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-6 pb-6">
                        <div className="space-y-6 pt-2">
                          {/* Description */}
                          <div>
                            <h4 className="text-plum font-medium mb-2 flex items-center gap-2">
                              <Target className="w-4 h-4 text-coral" /> Descripción
                            </h4>
                            <p className="text-plum-light text-sm leading-relaxed">{dis.descripcion}</p>
                          </div>

                          {/* Impact */}
                          <div>
                            <h4 className="text-plum font-medium mb-2 flex items-center gap-2">
                              <AlertTriangle className="w-4 h-4 text-amber-500" /> Impacto
                            </h4>
                            <p className="text-plum-light text-sm">{dis.impacto}</p>
                          </div>

                          {/* Neuroscience */}
                          <div className="bg-lavender/10 rounded-xl p-4">
                            <h4 className="text-lavender font-medium mb-2 flex items-center gap-2">
                              <Brain className="w-4 h-4" /> Explicación Neurocientífica
                            </h4>
                            <p className="text-plum-light text-sm leading-relaxed">{dis.neurociencia_explicacion}</p>
                          </div>

                          {/* Main Practice */}
                          <div className="bg-gradient-to-br from-coral/10 to-coral/5 rounded-xl p-4 border border-coral/20">
                            <h4 className="text-coral font-medium mb-3 flex items-center gap-2">
                              <Sparkles className="w-4 h-4" /> Práctica Principal: {dis.practica_principal?.nombre}
                            </h4>
                            <p className="text-plum-light text-sm mb-3">
                              <strong className="text-plum">Objetivo:</strong> {dis.practica_principal?.objetivo}
                            </p>
                            <div className="space-y-2">
                              <p className="text-plum text-sm font-medium">Instrucciones:</p>
                              <ol className="list-decimal list-inside space-y-1 text-plum-light text-sm">
                                {dis.practica_principal?.instrucciones?.map((inst, j) => (
                                  <li key={j}>{inst}</li>
                                ))}
                              </ol>
                            </div>
                            <div className="flex items-center gap-4 mt-4 text-sm">
                              <span className="text-plum-muted flex items-center gap-1">
                                <Clock className="w-3 h-3" /> {dis.practica_principal?.duracion}
                              </span>
                            </div>
                          </div>

                          {/* Micro Practice */}
                          {dis.micropractica && (
                            <div className="bg-white/50 rounded-xl p-4 border border-plum/10">
                              <h4 className="text-plum font-medium mb-2">
                                Micro-práctica: {dis.micropractica.nombre}
                              </h4>
                              <ul className="space-y-1 text-plum-light text-sm">
                                {dis.micropractica.instrucciones?.map((inst, j) => (
                                  <li key={j} className="flex items-start gap-2">
                                    <ChevronRight className="w-4 h-4 flex-shrink-0 mt-0.5 text-coral" />
                                    {inst}
                                  </li>
                                ))}
                              </ul>
                              <p className="text-plum-muted text-xs mt-2">{dis.micropractica.duracion}</p>
                            </div>
                          )}

                          {/* Journaling */}
                          {dis.journaling && dis.journaling.length > 0 && (
                            <div>
                              <h4 className="text-plum font-medium mb-3 flex items-center gap-2">
                                <BookOpen className="w-4 h-4 text-green-500" /> Journaling Prompts
                              </h4>
                              <ul className="space-y-2">
                                {dis.journaling.map((prompt, j) => (
                                  <li key={j} className="text-plum-light text-sm italic bg-green-50 rounded-lg p-3 border border-green-200">
                                    "{prompt}"
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </motion.div>
            </motion.section>
          )}

          {/* Roadmap Section */}
          <motion.section
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 
              variants={fadeInUp}
              className="font-heading text-2xl font-bold text-plum mb-2"
            >
              Tu Camino de Integración
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-plum-muted text-sm mb-6"
            >
              Plan de 90 días para recuperar tu coherencia
            </motion.p>

            <motion.div 
              variants={fadeInUp}
              className="space-y-4"
            >
              {roadmap && Object.entries(roadmap).map(([key, phase], i) => (
                <div key={key} className="glass-card-solid rounded-2xl p-5 relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-1.5 h-full bg-gradient-to-b from-coral to-lavender rounded-full" />
                  <div className="flex items-start gap-4 pl-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-coral/10 to-lavender/10 flex items-center justify-center flex-shrink-0">
                      <Calendar className="w-6 h-6 text-coral" />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg text-plum mb-1">{phase.titulo}</h3>
                      <p className="text-plum-muted text-xs mb-2 uppercase tracking-wider">
                        {key.replace("semana_", "Semanas ").replace("_", "-")}
                      </p>
                      <p className="text-plum-light text-sm mb-3">{phase.descripcion}</p>
                      <ul className="space-y-1">
                        {phase.tareas?.map((tarea, j) => (
                          <li key={j} className="text-plum-muted text-sm flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-400" />
                            {tarea}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </motion.div>
          </motion.section>

          {/* Ontological Reflection */}
          <motion.section
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <div className="glass-card-solid rounded-3xl p-8 text-center relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-coral/5 to-lavender/5" />
              <div className="relative z-10">
                <Sparkles className="w-8 h-8 text-coral mx-auto mb-4" />
                <h2 className="font-heading text-xl text-plum mb-4">Reflexión Ontológica</h2>
                <blockquote className="font-heading text-lg sm:text-xl text-plum/90 italic leading-relaxed max-w-2xl mx-auto">
                  "{reflexion_ontologica}"
                </blockquote>
              </div>
            </div>
          </motion.section>

          {/* CTA Section */}
          <motion.section
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center py-8"
          >
            <h2 className="font-heading text-2xl font-bold text-plum mb-4">
              ¿Necesitas Acompañamiento?
            </h2>
            <p className="text-plum-light mb-6 max-w-xl mx-auto">
              Si deseas profundizar en tu proceso de transformación, agenda una sesión 
              de coaching ontológico personalizada.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => window.open("https://www.alquimiadelalma.click", "_blank")}
                className="btn-coral px-8"
                data-testid="coaching-btn"
              >
                Agendar Sesión de Coaching
              </Button>
              <Button
                onClick={() => navigate("/evaluacion")}
                variant="outline"
                className="border-plum/20 text-plum hover:bg-plum/5"
                data-testid="retake-btn"
              >
                Volver a Evaluar
              </Button>
            </div>
          </motion.section>

          {/* Footer */}
          <footer className="text-center py-8 border-t border-plum/10">
            <img src={LOGO_URL} alt="Alquimia del Alma" className="h-12 w-12 rounded-full mx-auto mb-3 shadow-soft" />
            <p className="text-plum-muted text-xs max-w-xl mx-auto">
              Desarrollado por Julián López, Coach Ontológico y Master en Psicología Espiritual, 
              integrando neurociencia afectiva, ontología del lenguaje y prácticas contemplativas.
            </p>
            {/* Insignia oficial de Google Play: solo se escala, no se recorta ni recolorea. */}
            <a
              href="https://play.google.com/store/apps/details?id=com.alquimiadelalma.app"
              target="_blank"
              rel="noopener noreferrer"
              data-umami-event="app-play-coherencia-resultados"
              aria-label="Descargar la app Alquimia del Alma en Google Play"
              className="inline-block leading-none mt-6"
            >
              <img
                src="/google-play-badge.png"
                alt="Disponible en Google Play"
                width="150"
                height="58"
                loading="lazy"
                className="h-[58px] w-auto block"
              />
            </a>
            <p className="text-plum-muted/50 text-xs mt-4">
              © {new Date().getFullYear()} Alquimia del Alma
            </p>
          </footer>
        </div>
      </main>
    </div>
  );
}
